<template>
    <div>
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="mb-0 font-weight-bold">{{ $t('coop_my_applications') }}</h4>
            <b-button variant="primary" @click="openNewApplicationModal">
                {{ $t('coop_new_application') }}
            </b-button>
        </div>

        <!-- Applications Table -->
        <datatable :isLoading.sync="datatable.isLoading"
                   :columns="datatable.columns"
                   :rows="datatable.rows"
                   :total="datatable.total"
                   :queryParams="datatable.queryParams"
                   :lineNumbers="false"
                   @on-page-change="onPageChange"
                   @on-sort-change="onSortChange"
                   @on-per-page-change="onPerPageChange"
                   v-show="datatable.showTable"
        />

        <!-- New Application Modal -->
        <CommonModal ref="newApplicationModal" size="xl" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ $t('coop_appointment_calendar') }}
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-2">
                    <div class="calendar-container p-4 rounded mb-4" style="background-color: #f7f3ed; border: 1px solid #e6dfd5;">
                        <div class="d-flex justify-content-between align-items-center mb-4">
                            <!-- Week Navigation -->
                            <div class="d-flex align-items-center">
                                <b-button variant="light"
                                          size="sm"
                                          class="rounded-circle shadow-sm border mr-3"
                                          style="width: 32px; height: 32px; padding: 0;"
                                          @click="navigateWeek(-1)">
                                    <i class="ri-arrow-left-s-line" style="font-size: 1.2rem; line-height: 1;"></i>
                                </b-button>
                                <span class="font-weight-bold text-secondary">{{ getWeekRangeLabel() }}</span>
                                <b-button variant="light"
                                          size="sm"
                                          class="rounded-circle shadow-sm border ml-3"
                                          style="width: 32px; height: 32px; padding: 0;"
                                          @click="navigateWeek(1)">
                                    <i class="ri-arrow-right-s-line" style="font-size: 1.2rem; line-height: 1;"></i>
                                </b-button>
                            </div>
                        </div>

                        <!-- Calendar Grid -->
                        <b-row class="text-center mx-0 bg-white p-3 rounded shadow-sm">
                            <b-col v-for="day in weekDays"
                                   :key="day.dateStr"
                                   class="px-1 py-2 border-right last-no-border"
                                   style="min-width: 110px;">
                                <div class="font-weight-bold text-dark mb-1" style="font-size: 0.9rem;">
                                    {{ day.dayName }}
                                </div>
                                <div class="text-muted mb-3" style="font-size: 0.8rem;">
                                    {{ day.dateLabel }}
                                </div>

                                <!-- Slots -->
                                <div class="d-flex flex-column align-items-center">
                                    <b-button v-for="slot in timeSlots"
                                              :key="slot"
                                              size="sm"
                                              :variant="isSlotSelected(day.dateStr, slot) ? 'success' : 'light'"
                                              class="w-100 mb-2 py-2 font-weight-bold shadow-sm slot-btn"
                                              :style="getSlotStyle(day.dateStr, slot)"
                                              @click="selectSlot(day.dateStr, slot)">
                                        {{ slot }}
                                    </b-button>
                                </div>
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Create Request Action -->
                    <div class="text-center">
                        <b-button variant="primary"
                                  size="lg"
                                  class="px-5 font-weight-bold shadow"
                                  @click="submitApplication">
                            {{ $t('coop_create_appointment_request') }}
                        </b-button>
                    </div>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import CoopCounselingService from '@/services/CoopCounselingService';
    import moment from 'moment';
    import qs from 'qs';

    export default {
        name: 'CareerCounseling',
        components: {
            Datatable,
            CommonModal
        },
        data() {
            return {
                counselors: [],
                form: {
                    counselor_name: null,
                    appointment_date: '',
                    appointment_time: null
                },
                currentWeekStart: moment('2025-12-25'), // Matching wireframe start date
                timeSlots: ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'],
                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('coop_delete_application'),
                                    class: 'ri-delete-bin-line text-danger',
                                    callback: (row) => {
                                        this.deleteApplication(row.id);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('student_number'),
                            field: 'student_number',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('name'),
                            field: 'student_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('surname'),
                            field: 'student_surname',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_counselor'),
                            field: 'counselor_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_appointment_date'),
                            field: 'appointment_date',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_appointment_time'),
                            field: 'appointment_time',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('status'),
                            field: 'status',
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: 'id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        computed: {
            counselorOptions() {
                return this.counselors.map(c => ({ value: c, text: c }));
            },
            weekDays() {
                const days = [];
                // Generate 7 days starting from currentWeekStart
                const start = this.currentWeekStart.clone();

                // Set moment locale to Turkish if active language is tr, otherwise English
                const currentLocale = this.$i18n.locale === 'tr' ? 'tr' : 'en';

                for (let i = 0; i < 7; i++) {
                    const dayMoment = start.clone().add(i, 'days').locale(currentLocale);
                    days.push({
                        dateStr: dayMoment.format('YYYY-MM-DD'),
                        dayName: dayMoment.format('dddd'),
                        dateLabel: dayMoment.format('D MMMM'),
                        momentObj: dayMoment
                    });
                }
                return days;
            }
        },
        methods: {
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                const sortType = params[0].type == 'desc' ? '-' : '';
                this.datatable.queryParams.sort = sortType + params[0].field;
                this.getRows();
            },
            getRows() {
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {
                        ...this.datatable.queryParams
                    },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                return CoopCounselingService.getAll(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination.total;
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            loadDropdownData() {
                CoopCounselingService.getCounselors().then((res) => {
                    this.counselors = res.data;
                });
            },
            openNewApplicationModal() {
                this.resetForm();
                this.$refs.newApplicationModal.$refs.commonModal.show();
            },
            navigateWeek(direction) {
                this.currentWeekStart = this.currentWeekStart.clone().add(direction * 7, 'days');
            },
            getWeekRangeLabel() {
                if (this.weekDays.length === 0) return '';
                const start = this.weekDays[0].momentObj;
                const end = this.weekDays[6].momentObj;
                return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
            },
            selectSlot(dateStr, time) {
                // Parse dateStr back to formatted date
                const formattedDate = moment(dateStr).format('DD.MM.YYYY');
                this.form.appointment_date = formattedDate;
                this.form.appointment_time = time;
            },
            isSlotSelected(dateStr, time) {
                const formattedDate = moment(dateStr).format('DD.MM.YYYY');
                return this.form.appointment_date === formattedDate && this.form.appointment_time === time;
            },
            getSlotStyle(dateStr, time) {
                if (this.isSlotSelected(dateStr, time)) {
                    return {
                        backgroundColor: '#20c997',
                        borderColor: '#20c997',
                        color: '#ffffff'
                    };
                }
                return {
                    backgroundColor: '#ffffff',
                    color: '#6c757d',
                    border: '1px solid #dee2e6'
                };
            },
            submitApplication() {
                if (!this.form.counselor_name) {
                    this.$toast.error(this.$t('coop_please_select_counselor'));
                    return;
                }
                if (!this.form.appointment_date || !this.form.appointment_time) {
                    this.$toast.error(this.$t('coop_please_select_slot'));
                    return;
                }

                CoopCounselingService.create(this.form).then(() => {
                    this.$toast.success(this.$t('coop_application_created_success'));
                    this.$refs.newApplicationModal.$refs.commonModal.hide();
                    this.resetForm();
                    this.getRows();
                });
            },
            deleteApplication(id) {
                CoopCounselingService.remove(id).then(() => {
                    this.$toast.success(this.$t('coop_application_deleted_success'));
                    this.getRows();
                });
            },
            resetForm() {
                this.form = {
                    counselor_name: null,
                    appointment_date: '',
                    appointment_time: null
                };
            }
        },
        created() {
            this.getRows();
            this.loadDropdownData();
        }
    }
</script>

<style scoped>
    .last-no-border:last-child {
        border-right: none !important;
    }
    .slot-btn:hover {
        opacity: 0.9;
    }
</style>
